import { action, observable } from 'mobx'
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb'
import {
  ArchiveInventoryItemRequest,
  CreateInventoryItemRequest,
  GetManyInventoryItemsRequest,
  ImportInventoryItemsRequest,
  type InventoryItem as InventoryItemProto,
  InventoryItemProVisibility as InventoryItemProVisibilityProto,
  InventoryItemSupportingItem as InventoryItemSupportingItemProto,
  InventoryItemUpdateOptions,
  ListInventoryItemsForProvidersRequest,
  ListInventoryItemsRequest,
  ListWorkspacesWithInventoryItemsRequest,
  SetProVisibilityRequest,
  UnarchiveInventoryItemRequest,
  UpdateInventoryItemRequest,
  type WorkspaceWithInventoryItems as WorkspaceWithInventoryItemsProto,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import { collection } from '@/infra/collection'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { decimalToNumber, numberToDecimal } from '@/utils/grpc-util'
import { isNotNil } from '@/utils/object-util'
import { BadRequest } from 'fejl'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { PrimeDocumentTypeByShortIdFn } from '@/features/documents'
import { DocumentType } from '@/features/documents/DocumentType'
import { type InventoryApi } from '@/features/inventory/api/inventory-api'
import {
  InventoryItem,
  type InventoryItemProps,
  type InventoryItemProVisibility,
  type InventoryItemSupportingItem,
  type InventoryItemSupportingItemSpec,
  type InventoryItemTypeSpecification,
} from '@/features/inventory/InventoryItem'
import {
  WorkspaceWithInventoryItems,
  type WorkspaceWithInventoryItemsProps,
} from '@/features/inventory/WorkspaceWithInventoryItems'
import { type InventoryItemForProvider } from '@/features/inventory/InventoryItemForProvider'
import { type InventoryItemPricingValues } from '@/features/inventory/inventory-item-pricing-values'
import {
  mapInventoryItemForProviderProtoToProps,
  mapInventoryItemPriceProtoToPrice,
  mapInventoryItemPriceTypeProtoToPricing,
  mapInventoryItemProVisibilityProtoToProVisibility,
  mapInventoryItemProVisibilityToProto,
  mapInventoryItemTypeSpecificationProtoToProps,
  mapInventoryItemTypeSpecificationToProto,
  mapPricingValuesToClientPrice,
  mapPricingValuesToPriceTypeProto,
  mapPricingValuesToProPayout,
  mapSupportingItemsToProto,
  mapUpdateInventoryItemParamsToProps,
} from '@/features/inventory/inventory-item-mappers'
import {
  type ImportedInventoryItem,
  type InventoryItemExport,
  mapImportedInventoryItemResultProtoToImportedInventoryItem,
  mapInventoryItemExportToProto,
  mapInventoryItemProtoToExport,
} from '@/features/inventory/inventory-item-export'
import {
  buildInventoryItemSearchFilter,
  type InventoryItemSearchFilter,
} from '@/features/inventory/inventory-item-request-query-filter-builder'

export interface ListInventoryItemsQuery
  extends Partial<CursorPageQuery>, InventoryItemSearchFilter {
  workspaceIds: Id[]
  searchText?: string
  orderBy?: string
}

export interface ListInventoryItemsForProviderQuery
  extends Partial<CursorPageQuery>, InventoryItemSearchFilter {
  workspaceId: Id
  searchText?: string
  orderBy?: string
}

/**
 * Loads and caches service items.
 */
export class InventoryStore {
  /**
   * Total item count per workspace ID, or `'global'` for the global inventory.
   */
  @observable accessor totalItemCountPerWorkspace = new Map<string, number>()

  /**
   * Inventory items collection.
   */
  readonly inventoryItems = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new InventoryItem(),
  })

  /**
   * Workspaces that have inventory items.
   */
  readonly workspacesWithInventoryItems = collection({
    getDataId: (d) => d.workspaceId,
    getModelId: (m) => m.workspaceId,
    create: () => new WorkspaceWithInventoryItems(),
  })

  constructor(
    private readonly _api: InventoryApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeDocumentTypeByShortId: PrimeDocumentTypeByShortIdFn,
  ) {}

  /**
   * Lists the inventory items of the given workspaces, or the global
   * inventory when there are none.
   *
   * @param query
   * @param signal
   */
  async listInventoryItems(
    {
      workspaceIds,
      limit,
      after: pageToken,
      searchText,
      orderBy,
      ...searchFilter
    }: ListInventoryItemsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<InventoryItem>> {
    const request = new ListInventoryItemsRequest()
    const filter = buildInventoryItemSearchFilter(searchFilter)

    if (workspaceIds.length) {
      request.setWorkspaceIdsList(workspaceIds.filter(isNotNil).map(Id.assert))
    }
    if (limit) {
      request.setPageSize(limit)
    }
    if (pageToken) {
      request.setPageToken(pageToken)
    }
    if (filter) {
      request.setFilter(filter)
    }
    if (orderBy) {
      request.setOrderBy(orderBy)
    }
    if (searchText) {
      request.setSearchText(searchText)
    }

    const result = await this._api.inventoryItems.listInventoryItems(request)
    signal?.throwIfAborted()

    const workspaceId: Id =
      workspaceIds?.length > 0 ? workspaceIds[0] : 'global'
    this._setTotalItemCountPerWorkspace(
      Id.assert(workspaceId),
      result.getTotalSize(),
    )

    return {
      data: this.inventoryItems.set(
        result
          .getInventoryItemsList()
          .map((proto) => this._mapInventoryItemProtoToProps(proto)),
      ),
      total: result.getTotalSize(),
      next: result.getNextPageToken(),
    }
  }

  /**
   * Lists the inventory items a pro may add to a job in the workspace.
   *
   * @param query
   * @param signal
   */
  async listInventoryItemsForProvider(
    {
      workspaceId,
      limit,
      after: pageToken,
      searchText,
      orderBy,
      ...searchFilter
    }: ListInventoryItemsForProviderQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<InventoryItemForProvider>> {
    const request = new ListInventoryItemsForProvidersRequest()
    const filter = buildInventoryItemSearchFilter(searchFilter)

    if (workspaceId) {
      request.setWorkspaceId(Id.assert(workspaceId))
    }
    if (limit) {
      request.setPageSize(limit)
    }
    if (pageToken) {
      request.setPageToken(pageToken)
    }
    if (filter) {
      request.setFilter(filter)
    }
    if (orderBy) {
      request.setOrderBy(orderBy)
    }
    if (searchText) {
      request.setSearchText(searchText)
    }

    const result =
      await this._api.inventoryItems.listInventoryItemsForProviders(request)
    signal?.throwIfAborted()

    return {
      data: result
        .getInventoryItemsList()
        .map(mapInventoryItemForProviderProtoToProps),
      next: result.getNextPageToken(),
    }
  }

  /**
   * Fetches the workspaces that have inventory items, replacing the
   * previously fetched ones.
   *
   * @param signal
   */
  async fetchWorkspacesWithInventoryItems(signal?: AbortSignal) {
    const request = new ListWorkspacesWithInventoryItemsRequest()

    const result =
      await this._api.inventoryItems.listWorkspacesWithInventoryItems(request)
    signal?.throwIfAborted()

    return this.workspacesWithInventoryItems.reset(
      result
        .getWorkspacesList()
        .map((proto) =>
          this._mapWorkspaceWithInventoryItemsProtoToProps(proto),
        ),
    )
  }

  /**
   * Creates an inventory item.
   *
   * @param payload
   */
  async createInventoryItem(payload: CreateInventoryItemParams) {
    const supportingItems = mapSupportingItemsToProto(payload.supportingItems)
    const typeSpecification = mapInventoryItemTypeSpecificationToProto(
      payload.typeSpecification,
    )

    const request = new CreateInventoryItemRequest()
      .setName(payload.name)
      .setDescription(payload.description)
      .setCourtesyAmount(payload.courtesyAmount)
      .setPrice(numberToDecimal(mapPricingValuesToClientPrice(payload.pricing)))
      .setProPrice(
        numberToDecimal(mapPricingValuesToProPayout(payload.pricing)),
      )
      .setPriceType(mapPricingValuesToPriceTypeProto(payload.pricing))
      .setSupportingItemsList(supportingItems)
      .setTypeSpecification(typeSpecification)

    if (Id.some(payload.workspace)) {
      request.setWorkspaceId(payload.workspace)
    }

    const item = await this._api.inventoryItems.createInventoryItem(request)

    if (payload.hideFromPro) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(item.getId())
        .setVisibility(InventoryItemProVisibilityProto.HIDDEN)

      await this._api.inventoryItems.setProVisibility(setHiddenFromProRequest)
    }
  }

  /**
   * Updates an inventory item.
   *
   * @param id
   * @param payload
   */
  async updateInventoryItem(id: Id, payload: UpdateInventoryItemParams) {
    const inventoryItemToBeUpdated = this.inventoryItems.find(
      (i) => i.id === id,
    )

    const supportingItems = mapSupportingItemsToProto(payload.supportingItems)
    const typeSpecification =
      payload.typeSpecification !== null
        ? mapInventoryItemTypeSpecificationToProto(payload.typeSpecification)
        : undefined

    const options = new InventoryItemUpdateOptions()
      .setName(payload.name)
      .setDescription(payload.description)
      .setCourtesyAmount(payload.courtesyAmount)
      .setPrice(numberToDecimal(mapPricingValuesToClientPrice(payload.pricing)))
      .setProPrice(
        numberToDecimal(mapPricingValuesToProPayout(payload.pricing)),
      )
      .setSupportingItemsList(supportingItems)
      .setTypeSpecification(typeSpecification)

    const fieldMask = new FieldMask().setPathsList([
      'name',
      'description',
      'courtesy_amount',
      'price',
      'pro_price',
      'supporting_items',
      'type_specification',
    ])

    const request = new UpdateInventoryItemRequest()
      .setId(Id.assert(id))
      .setOptions(options)
      .setUpdateMask(fieldMask)

    const response = await this._api.inventoryItems.updateInventoryItem(request)
    inventoryItemToBeUpdated?.set(
      mapUpdateInventoryItemParamsToProps(payload, response.getVersionId()),
    )

    if (payload.proVisibility !== inventoryItemToBeUpdated?.proVisibility) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(Id.assert(id))
        .setVisibility(
          mapInventoryItemProVisibilityToProto(payload.proVisibility),
        )

      const visibilityResponse =
        await this._api.inventoryItems.setProVisibility(setHiddenFromProRequest)
      inventoryItemToBeUpdated?.set({
        proVisibility: mapInventoryItemProVisibilityProtoToProVisibility(
          visibilityResponse.getVisibility(),
        ),
      })
    }
  }

  /**
   * Overrides a global inventory item for a workspace.
   *
   * @param payload
   */
  async overrideInventoryItem(payload: OverrideInventoryItemParams) {
    const supportingItems = mapSupportingItemsToProto(payload.supportingItems)
    const typeSpecification = mapInventoryItemTypeSpecificationToProto(
      payload.typeSpecification,
    )

    const request = new CreateInventoryItemRequest()
      .setName(payload.name)
      .setDescription(payload.description)
      .setCourtesyAmount(payload.courtesyAmount)
      .setPrice(numberToDecimal(mapPricingValuesToClientPrice(payload.pricing)))
      .setProPrice(
        numberToDecimal(mapPricingValuesToProPayout(payload.pricing)),
      )
      .setPriceType(mapPricingValuesToPriceTypeProto(payload.pricing))
      .setWorkspaceId(Id.assert(payload.workspace))
      .setRootId(Id.assert(payload.rootId))
      .setSupportingItemsList(supportingItems)
      .setTypeSpecification(typeSpecification)

    const createdItem =
      await this._api.inventoryItems.createInventoryItem(request)

    if (payload.hideFromPro) {
      const setHiddenFromProRequest = new SetProVisibilityRequest()
        .setId(createdItem.getId())
        .setVisibility(InventoryItemProVisibilityProto.HIDDEN)

      await this._api.inventoryItems.setProVisibility(setHiddenFromProRequest)
    }

    await this.fetchWorkspacesWithInventoryItems()
  }

  /**
   * Archives an inventory item.
   *
   * @param id
   */
  async archiveInventoryItem(id: Id) {
    const request = new ArchiveInventoryItemRequest().setId(Id.assert(id))

    const response =
      await this._api.inventoryItems.archiveInventoryItem(request)
    this.inventoryItems.get(id)?.set({ archived: response.getArchived() })
  }

  /**
   * Unarchives an inventory item.
   *
   * @param id
   */
  async unarchiveInventoryItem(id: Id) {
    const request = new UnarchiveInventoryItemRequest().setId(Id.assert(id))

    const response =
      await this._api.inventoryItems.unarchiveInventoryItem(request)
    this.inventoryItems.get(id)?.set({ archived: response.getArchived() })
  }

  /**
   * Fetches the workspace's inventory items with the given IDs.
   *
   * @param ids
   * @param workspaceId
   * @param signal
   */
  async fetchInventoryItemsByIds(
    ids: Id[],
    workspaceId: Id,
    signal?: AbortSignal,
  ) {
    const request = new GetManyInventoryItemsRequest()
      .setIdsList(ids.map(Id.assert))
      .setWorkspaceId(Id.assert(workspaceId))

    const result = await this._api.inventoryItems.getManyInventoryItems(request)
    signal?.throwIfAborted()

    return this.inventoryItems.set(
      result
        .getInventoryItemsList()
        .map((op) => op.getItem())
        .filter(isNotNil)
        .map((item) => this._mapInventoryItemProtoToProps(item)),
    )
  }

  /**
   * Fetches the workspace's service items with the given IDs as exports.
   *
   * @param ids
   * @param workspaceId
   * @param signal
   */
  async fetchInventoryItemExports(
    ids: Id[],
    workspaceId: Id,
    signal?: AbortSignal,
  ): Promise<InventoryItemExport[]> {
    const request = new GetManyInventoryItemsRequest()
      .setIdsList(ids.map(Id.assert))
      .setWorkspaceId(Id.assert(workspaceId))

    const result = await this._api.inventoryItems.getManyInventoryItems(request)
    signal?.throwIfAborted()

    return result
      .getInventoryItemsList()
      .map((op) => op.getItem())
      .filter(isNotNil)
      .map(mapInventoryItemProtoToExport)
  }

  /**
   * Imports exported service items into the workspace. An item the workspace
   * has imported before is reused rather than imported again.
   *
   * @param items
   * @param workspaceId
   */
  async importInventoryItems(
    items: InventoryItemExport[],
    workspaceId: Id,
  ): Promise<ImportedInventoryItem[]> {
    const request = new ImportInventoryItemsRequest()
      .setItemsToImportList(items.map(mapInventoryItemExportToProto))
      .setWorkspaceId(Id.assert(workspaceId))

    const result = await this._api.inventoryItems.importInventoryItems(request)
    return result
      .getNewItemsList()
      .map(mapImportedInventoryItemResultProtoToImportedInventoryItem)
  }

  private _mapInventoryItemProtoToProps(
    proto: InventoryItemProto,
  ): Partial<InventoryItemProps> {
    const priceType = mapInventoryItemPriceTypeProtoToPricing(
      proto.getPriceType(),
    )
    const price = proto.getPrice()
    const proPrice = proto.getProPrice()
    const workspaceId = proto.getWorkspaceId()
    const typeSpecification = proto.getTypeSpecification()

    if (!typeSpecification) {
      throw new BadRequest('Type specification not found')
    }

    return {
      id: proto.getId(),
      versionId: proto.getVersionId(),
      shortId: proto.getShortId(),
      name: proto.getName(),
      description: proto.getDescription(),
      proVisibility: mapInventoryItemProVisibilityProtoToProVisibility(
        proto.getProVisibility(),
      ),
      archived: proto.getArchived(),
      workspace: workspaceId ? this._primeWorkspace({ id: workspaceId }) : null,
      createdBy: this._primeUser({ publicId: proto.getCreatedBy() }),
      version: proto.getVersion(),
      dateCreated: proto.getDateCreated()?.toDate(),
      courtesyAmount: proto.getCourtesyAmount(),
      price: price
        ? mapInventoryItemPriceProtoToPrice(decimalToNumber(price), priceType)
        : undefined,
      proPrice: proPrice
        ? mapInventoryItemPriceProtoToPrice(
            decimalToNumber(proPrice),
            priceType,
          )
        : undefined,
      rootId: proto.getRootId() || undefined,
      priceType,
      supportingItems: proto
        .getSupportingItemsList()
        .map((item) => this._mapSupportingItemProtoToProps(item)),
      typeSpecification:
        mapInventoryItemTypeSpecificationProtoToProps(typeSpecification),
    }
  }

  private _mapSupportingItemProtoToProps(
    proto: InventoryItemSupportingItemProto,
  ): InventoryItemSupportingItem {
    const spec = proto.getSpec()
    if (!spec) {
      throw new BadRequest('No supporting item spec type found')
    }

    return {
      required: proto.getRequired(),
      spec: this._mapSupportingItemSpecProtoToProps(spec),
    }
  }

  private _mapSupportingItemSpecProtoToProps(
    spec: InventoryItemSupportingItemProto.Spec,
  ): InventoryItemSupportingItemSpec {
    const TypeCase = InventoryItemSupportingItemProto.Spec.TypeCase
    switch (spec.getTypeCase()) {
      case TypeCase.DOCUMENT: {
        const document = spec.getDocument()
        if (!document) {
          throw new BadRequest('Supporting item spec document not found')
        }
        return {
          type: 'Document',
          documentType:
            this._primeDocumentTypeByShortId(document.getTypeShortId()) ??
            DocumentType.none,
        }
      }
      case TypeCase.BUSINESS_INFO:
        return { type: 'BusinessInfo' }
      case TypeCase.BUSINESS_REFUND_METHOD:
        return { type: 'BusinessRefundMethodInfo' }
      case TypeCase.DEPENDENTS_INFO:
        return { type: 'DependentsInfo' }
      case TypeCase.NOTES:
        return { type: 'Notes' }
      case TypeCase.PERSONAL_INFO:
        return { type: 'PersonalInfo' }
      case TypeCase.PERSONAL_REFUND_METHOD:
        return { type: 'PersonalRefundMethodInfo' }
      case TypeCase.SPOUSAL_INFO:
        return { type: 'SpousalInfo' }
      case TypeCase.TYPE_NOT_SET:
        throw new BadRequest('Unsupported supporting item spec type')
    }
  }

  private _mapWorkspaceWithInventoryItemsProtoToProps(
    proto: WorkspaceWithInventoryItemsProto,
  ): WorkspaceWithInventoryItemsProps {
    return {
      workspaceId: proto.getWorkspaceId(),
      workspace:
        this._primeWorkspace({ id: proto.getWorkspaceId() }) ?? Workspace.none,
      total: proto.getTotal(),
    }
  }

  @action.bound
  private _setTotalItemCountPerWorkspace(workspaceId: string, count: number) {
    this.totalItemCountPerWorkspace.set(workspaceId, count)
  }
}

export interface CreateInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  workspace: Id
  supportingItems: InventoryItemSupportingItem[]
  hideFromPro: boolean
  typeSpecification: InventoryItemTypeSpecification
}

export interface UpdateInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  supportingItems: InventoryItemSupportingItem[]
  proVisibility: InventoryItemProVisibility
  typeSpecification: InventoryItemTypeSpecification | null
}

export interface OverrideInventoryItemParams {
  name: string
  description: string
  courtesyAmount: number
  pricing: InventoryItemPricingValues
  workspace: Id
  rootId: Id
  supportingItems: InventoryItemSupportingItem[]
  hideFromPro: boolean
  typeSpecification: InventoryItemTypeSpecification
}
